import { useState } from 'react';
import { Play, MessageSquare, Monitor, Bell, Zap, Users, GitBranch, Settings, ArrowRight, FolderOpen, Layers, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/i18n/LanguageContext';
import { BlockType, WorkflowBlock, BlockGroup } from '@/types/workflow';
import { LayerTree } from '@/components/workflow/LayerTree';
import { BRAND_NAVY_VAR } from '@/constants/brandColors';
import { MobileSheet } from './ui/MobileSheet';


const toolTypes: { type: BlockType; icon: React.ComponentType<{ className?: string }>; labelKey: string }[] = [
  { type: 'create-user', icon: Play, labelKey: 'createUser' },
  { type: 'disable-mailbox', icon: MessageSquare, labelKey: 'disableMailbox' },
  { type: 'delete-m365', icon: Monitor, labelKey: 'deleteM365' },
  { type: 'send-email', icon: Bell, labelKey: 'sendEmail' },
  { type: 'webhook', icon: Zap, labelKey: 'webhook' },
  { type: 'add-to-group', icon: Users, labelKey: 'addToGroup' },
  { type: 'condition', icon: GitBranch, labelKey: 'condition' },
  { type: 'general', icon: Settings, labelKey: 'generalFunction' },
];

interface MobileToolSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddBlock: (type: BlockType) => void;
  connectMode: boolean;
  groupMode: boolean;
  groupingCount: number;
  onToggleConnectMode: () => void;
  onToggleGroupMode: () => void;
  blocks: WorkflowBlock[];
  groups: BlockGroup[];
  currentLayerGroupId: string | null;
  onNavigateToLayer: (groupId: string | null) => void;
}

export function MobileToolSheet({
  open, onOpenChange, onAddBlock, connectMode, groupMode, groupingCount,
  onToggleConnectMode, onToggleGroupMode, blocks, groups, currentLayerGroupId, onNavigateToLayer,
}: MobileToolSheetProps) {
  const { t } = useTranslation();
  const [layersOpen, setLayersOpen] = useState(false);
  const hasCollapsedGroups = groups.some((g) => g.collapsed);

  return (
    <MobileSheet open={open} onOpenChange={onOpenChange} title={t('tools')} maxHeightDvh={75}>
      {/* Block palette — 2 columns */}
      <div className="px-4 pb-3">
        <h3 className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: BRAND_NAVY_VAR, opacity: 0.5 }}>
          {t('tools')} — Tap to add
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {toolTypes.map((tool) => {
            const Icon = tool.icon;
            return (
              <button
                key={tool.type}
                onClick={() => { onAddBlock(tool.type); onOpenChange(false); }}
                className="flex items-center gap-2 p-3 rounded-xl border border-border bg-card active:bg-primary/10 transition-colors text-left"
                style={{ minHeight: '48px', color: BRAND_NAVY_VAR }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'hsl(var(--brand-navy) / 0.1)' }}>
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-xs font-medium leading-tight">{t(tool.labelKey)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mode toggles */}
      <div className="px-4 pb-3 flex gap-2">
        <Button
          onClick={onToggleConnectMode}
          variant={connectMode ? 'destructive' : 'outline'}
          className="flex-1 h-12 gap-2"
        >
          <ArrowRight className="w-4 h-4" />
          {connectMode ? t('connecting') : t('connectBlocks')}
        </Button>
        <Button
          onClick={onToggleGroupMode}
          variant={groupMode ? 'destructive' : 'outline'}
          className="flex-1 h-12 gap-2"
        >
          <FolderOpen className="w-4 h-4" />
          {groupMode ? `${t('grouping')} (${groupingCount})` : t('groupBlocks')}
        </Button>
      </div>

      {/* Layers */}
      {hasCollapsedGroups && (
        <div className="px-4 pb-4">
          <button
            onClick={() => setLayersOpen(!layersOpen)}
            className="flex items-center gap-2 w-full text-xs font-semibold uppercase tracking-wider mb-2"
            style={{ color: BRAND_NAVY_VAR, opacity: 0.5 }}
          >
            {layersOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            <Layers className="w-3 h-3" /> Layers
          </button>
          {layersOpen && (
            <LayerTree groups={groups} blocks={blocks} currentGroupId={currentLayerGroupId} onNavigate={onNavigateToLayer} />
          )}
        </div>
      )}
    </MobileSheet>
  );
}
